import SPLIT_FEATURES from 'containers/SplitContext/features'
import { shouldHideOperationsNavSlug } from './fulfilmentPickingConfig'

const LEGACY_DBP_FLAG = SPLIT_FEATURES.BACK_DBP_LEGACY_PAGES_ENABLED

export const HR_DESIGNATION_ROLES_SUBMENU_PATH = 'hr/designation-roles'

const HR_DESIGNATION_ROLES_SUBMENU_SLUG =
  HR_DESIGNATION_ROLES_SUBMENU_PATH.split('/')[1]

/**
 * Split-only whitelist for legacy DBP routes (Harness/Split dynamic config JSON).
 * Routes **listed** in config are shown when treatment is `on`, hidden/blocked when `off`.
 * Routes **not** listed are unaffected by this flag (always behave like normal app routes).
 *
 * Shape: `{ "rootMenus": ["gift-card"], "submenuPaths": ["operations/pickers","hr/designation-roles"] }`
 * - `rootMenus`: first URL segment for whole sections (blocks `/gift-card/...`).
 * - `submenuPaths`: `main/sub` pairs (blocks `/main/sub/...`).
 * Missing config, invalid JSON, or omitted keys → empty arrays for that key (nothing legacy-controlled).
 */
function normalizedLegacyDbpConfig(parsed) {
  if (!parsed || typeof parsed !== 'object') {
    return { rootMenus: [], submenuPaths: [] }
  }
  return {
    rootMenus: Array.isArray(parsed.rootMenus) ? parsed.rootMenus : [],
    submenuPaths: Array.isArray(parsed.submenuPaths) ? parsed.submenuPaths : [],
  }
}

function parseLegacyDbpFlagConfig(splits) {
  const raw = splits?.[LEGACY_DBP_FLAG]?.config
  if (raw == null || raw === '') {
    return { rootMenus: [], submenuPaths: [] }
  }
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
    return normalizedLegacyDbpConfig(parsed)
  } catch {
    return { rootMenus: [], submenuPaths: [] }
  }
}

/** Root menu keys listed in Split for this flag (whitelist only). */
export function getLegacyDbpRootMenus(splits) {
  return parseLegacyDbpFlagConfig(splits).rootMenus
}

/** `main/sub` paths listed in Split for this flag (whitelist only). */
export function getLegacyDbpSubmenuPaths(splits) {
  return parseLegacyDbpFlagConfig(splits).submenuPaths
}

function getLegacyDbpSubmenuPathsSet(splits) {
  return new Set(getLegacyDbpSubmenuPaths(splits))
}

/**
 * True when treatment is `off`. Only routes listed in Split config are hidden/blocked;
 * this does not mean “all legacy routes off” unless those routes appear in the whitelist JSON.
 */
export function isLegacyDbpKillSwitchOff(splits) {
  return splits?.[LEGACY_DBP_FLAG]?.treatment === 'off'
}

/**
 * @deprecated Prefer {@link isLegacyDbpKillSwitchOff} or route checks — semantics are whitelist-based.
 * Still true when treatment is `off`.
 */
export function isLegacyDbpPagesDisabled(splits) {
  return isLegacyDbpKillSwitchOff(splits)
}

/**
 * Use this mapping to map each root menu name or submenu name to its corresponding
 * feature flag name, (if any)
 * Make sure to use full path for submenu as there could be similar submenu name in different menu items.
 * Key in below object is important as it is a connection between split flag and actual menu
 *
 * Legacy DBP paths are controlled only via Split whitelist (getLegacyDbpRootMenus / getLegacyDbpSubmenuPaths).
 *
 * hr/designation-roles: hidden when DESIGNATION_ROLE is off, or when listed under submenuPaths and legacy kill is off.
 */
const MenusFeatureFlagMapping = {
  'admin/preorder': SPLIT_FEATURES.PREORDER_STAFF,
  'marketing/campaign-labels': SPLIT_FEATURES.BACK_DIS_CAMPAIGN_LABEL,
  'customer-support/dashboard': SPLIT_FEATURES.LOYALTY_LINKPASS_FEATURES,
  'catalogue/product-cache-clear': SPLIT_FEATURES.BACK_DIS_CACHE_CLEAR,
  'catalogue/sku-media-upload': SPLIT_FEATURES.BACK_SKU_MEDIA_UPLOAD,
  'marketing/games': SPLIT_FEATURES.DYNAMICS_BACKOFFICE_GAME_SETTINGS,
  'marketing/swa-banners': SPLIT_FEATURES.BACK_PDM_SWA_BANNERS,
  'marketing/scan-rewards': SPLIT_FEATURES.BACK_OMNI_SCAN_REWARDS,
  'app-home/whitelist-domain': SPLIT_FEATURES.BACK_OMNI_WHITELIST_DOMAIN,
}

function shouldHideDesignationRolesSubmenu(splits) {
  const designationOff =
    splits?.[SPLIT_FEATURES.DESIGNATION_ROLE]?.treatment === 'off'
  const killOff = isLegacyDbpKillSwitchOff(splits)
  const designationRolesListed = getLegacyDbpSubmenuPathsSet(splits).has(
    HR_DESIGNATION_ROLES_SUBMENU_PATH
  )
  return designationOff || (killOff && designationRolesListed)
}

function spliceSubmenu(menu, rootMenu, subMenuSlug) {
  if (!menu[rootMenu] || !Array.isArray(menu[rootMenu])) {
    return
  }
  const index = menu[rootMenu].indexOf(subMenuSlug)
  if (index !== -1) {
    menu[rootMenu].splice(index, 1)
  }
}

/**
 * Returns true if the pathname should not load (blocked legacy whitelist route when kill is off, or designation roles rules).
 */
export function isLegacyDbpRouteBlocked(pathname, splits) {
  if (!pathname || !splits) {
    return false
  }
  const segments = pathname.split('/').filter(Boolean)
  if (!segments.length) {
    return false
  }

  const menuPath = segments[0]
  const submenuPath = segments[1]
  const legacyRoots = getLegacyDbpRootMenus(splits)
  const legacySubmenus = getLegacyDbpSubmenuPathsSet(splits)

  const pathKey =
    submenuPath !== undefined ? `${menuPath}/${submenuPath}` : null

  if (pathKey === HR_DESIGNATION_ROLES_SUBMENU_PATH) {
    return shouldHideDesignationRolesSubmenu(splits)
  }

  if (!isLegacyDbpKillSwitchOff(splits)) {
    return false
  }

  if (legacyRoots.includes(menuPath)) {
    return true
  }

  if (pathKey && legacySubmenus.has(pathKey)) {
    return true
  }

  return false
}

/**
 * This function will remove menus which have feature flag mapping to it and
 * treatments are turned off.
 * @param {*} splits
 * @param {*} menu
 */
export const checkForFeatureFlags = (splits, menu) => {
  try {
    const entries = Object.entries(menu)
    for (const entry of entries) {
      const rootMenu = entry[0]
      const subMenus = entry[1]

      checkMenuItems(menu, rootMenu, splits)
      checkSubMenuItems(menu, rootMenu, subMenus, splits)
    }
  } catch (err) {
    console.error(err)
  }
  return menu
}

/*
  Technical note:
  objects and arrays passed to function are reference to original object and array.
  Changing the value inside the function will change the referenced object and array
*/
// Check root menu items
function checkMenuItems(menu, rootMenu, splits) {
  if (getLegacyDbpRootMenus(splits).includes(rootMenu)) {
    if (isLegacyDbpKillSwitchOff(splits)) {
      delete menu[rootMenu]
    }
    return
  }
  const menuFlag = MenusFeatureFlagMapping[rootMenu]
  if (menuFlag) {
    const targetFlag = splits[menuFlag]
    if (targetFlag && targetFlag.treatment === 'off') {
      delete menu[rootMenu]
    }
  }
}

function shouldHideFulfilmentPickingSubmenu(rootMenu, subMenuPath, splits) {
  if (rootMenu !== 'operations') {
    return false
  }
  const subMenuSlug = subMenuPath.split('/')[1]
  return shouldHideOperationsNavSlug(splits, subMenuSlug)
}

// Check sub menu items
function checkSubMenuItems(menu, rootMenu, subMenus, splits) {
  if (!Array.isArray(subMenus)) {
    return
  }
  const legacySubmenuSet = getLegacyDbpSubmenuPathsSet(splits)
  subMenus
    .map((subpath) => `${rootMenu}/${subpath}`)
    .forEach((subMenuPath) => {
      if (subMenuPath === HR_DESIGNATION_ROLES_SUBMENU_PATH) {
        if (shouldHideDesignationRolesSubmenu(splits)) {
          spliceSubmenu(menu, rootMenu, HR_DESIGNATION_ROLES_SUBMENU_SLUG)
        }
        return
      }

      if (shouldHideFulfilmentPickingSubmenu(rootMenu, subMenuPath, splits)) {
        spliceSubmenu(menu, rootMenu, subMenuPath.split('/')[1])
        return
      }

      if (legacySubmenuSet.has(subMenuPath)) {
        if (isLegacyDbpKillSwitchOff(splits)) {
          const subMenuPathName = subMenuPath.split('/')[1]
          spliceSubmenu(menu, rootMenu, subMenuPathName)
        }
        return
      }

      const subMenuFlag = MenusFeatureFlagMapping[subMenuPath]
      if (subMenuFlag) {
        const targetFlag = splits[subMenuFlag]
        if (
          (targetFlag &&
            targetFlag.treatment === 'off' &&
            subMenuPath !== 'customer-support/dashboard') ||
          (subMenuPath === 'customer-support/dashboard' &&
            targetFlag.treatment === 'on')
        ) {
          const subMenuPathName = subMenuPath.split('/')[1]
          spliceSubmenu(menu, rootMenu, subMenuPathName)
        }
      }
    })
}
